/**
 * Pure helpers behind the Reports page (ui/src/pages/reports/Reports.tsx).
 *
 * The page's cards are the launched tools of the catalog's `reports`
 * department that the signed-in user is permitted to open (the same rule the
 * sidebar uses), grouped by each tool's `group`, which on Reports is the topic.
 */

import { findDepartment, type Tool } from '@/data/tools.config';
import { visibleToolsFor } from '@/lib/toolVisibility';

export const REPORTS_DEPT_ID = 'reports';

/** Topic headings, in page order. A topic with no visible report is skipped. */
export const REPORT_TOPICS = ['Sales', 'Loyalty', 'Inventory', 'Finance', 'Store Analytics'] as const;

/** Reports this user can open, in catalog order. */
export function visibleReports(perms: string[] | undefined, superAdmin: boolean): Tool[] {
  const dept = findDepartment(REPORTS_DEPT_ID);
  return dept ? visibleToolsFor(dept, perms, superAdmin) : [];
}

/** Case-insensitive match on the report's name and description. */
export function matchesReportSearch(tool: Tool, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return tool.name.toLowerCase().includes(q) || tool.desc.toLowerCase().includes(q);
}

/**
 * Reports grouped under REPORT_TOPICS in that order. A report whose group is
 * missing or not a known topic lands under "Other" at the end, so a new card
 * is never silently dropped.
 */
export function groupReportsByTopic(tools: Tool[]): { topic: string; tools: Tool[] }[] {
  const known = new Set<string>(REPORT_TOPICS);
  const groups = REPORT_TOPICS.map((topic) => ({
    topic: topic as string,
    tools: tools.filter((t) => t.group === topic),
  }));
  const other = tools.filter((t) => !t.group || !known.has(t.group));
  if (other.length > 0) groups.push({ topic: 'Other', tools: other });
  return groups.filter((g) => g.tools.length > 0);
}

/** Ids in `order` that are also in `tools`, mapped back to the tools, order kept. */
export function pickInOrder(order: string[], tools: Tool[]): Tool[] {
  const byId = new Map(tools.map((t) => [t.id, t]));
  return order.flatMap((id) => {
    const t = byId.get(id);
    return t ? [t] : [];
  });
}
